import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import GreenButton from '../../components/GreenButton';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import WallEditDialog from './WallEditDialog';

import {
  Button,
  Grid,
  Paper,
  List,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import FolderEditDialog from 'features/wallFolders/FolderEditDialog';
import { WallFolder, Wall } from 'types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import WallListItem from 'features/wallFolders/WallListItem';
import FolderListItem from 'features/wallFolders/FolderListItem';
import WallsWithoutFoldersContainer from 'features/wallFolders/WallsWithoutFoldersContainer';

import { useWall } from 'features/walls/api/getWall';
import { useDeleteWall } from 'features/walls/api/deleteWall';
import { useFolders } from 'features/wallFolders/api/getFolders';
import { useDeleteFolder } from 'features/wallFolders/api/deleteFolder';
import ExportWallDialog from './ExportWallDialog';
import ImportWallDialog from './ImportWallDialog';

const ManageWallsPage = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { data: folders } = useFolders({ userId: user?.id });
  const deleteFolder = useDeleteFolder({});

  const [selectedWallId, setSelectedWallId] = useState<number | undefined>(
    undefined
  );
  const { data: selectedWall } = useWall({ wallId: selectedWallId || -1 });
  const [selectedFolder, setSelectedFolder] = useState<WallFolder | undefined>(
    undefined
  );

  const [foldersOpenStatus, setFoldersOpenStatus] = useState<{
    [id: number]: boolean;
  }>({});

  const deleteWallMutation = useDeleteWall({});

  const [wallEditDialogOpen, setWallEditDialogOpen] = useState(false);
  const [folderEditDialogOpen, setFolderEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const handleCreateWallClicked = async () => {
    setSelectedWallId(-1);
    setWallEditDialogOpen(!wallEditDialogOpen);
  };

  const handleCreateFolderClicked = async () => {
    setSelectedFolder(undefined);
    setFolderEditDialogOpen(true);
  };

  const handleDeleteDialogOpen = () => {
    if (selectedWallId) {
      setDeleteDialogOpen(true);
    } else if (selectedFolder) {
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteClicked = async () => {
    if (selectedWallId) {
      await deleteWallMutation.mutateAsync(selectedWallId);
      setSelectedWallId(undefined);
    } else if (selectedFolder) {
      await deleteFolder.mutateAsync(selectedFolder.id);
    }
  };

  const handleWallClicked = (wall: Wall) => {
    if (selectedWallId === wall.id) setSelectedWallId(undefined);
    else setSelectedWallId(wall.id);
    setSelectedFolder(undefined);
  };

  const handleFolderClicked = (folder: WallFolder) => {
    if (folder.id === selectedFolder?.id) setSelectedFolder(undefined);
    else setSelectedFolder(folder);
    setSelectedWallId(undefined);
    const currentFolderStatus = foldersOpenStatus[folder.id];
    const newFolderStatusState = { ...foldersOpenStatus };
    newFolderStatusState[folder.id] = !currentFolderStatus;
    setFoldersOpenStatus(newFolderStatusState);
  };

  const handleEditClicked = () => {
    if (selectedWallId) {
      navigate('/edit-wall/' + selectedWallId);
    } else if (selectedFolder) {
      setFolderEditDialogOpen(true);
    }
  };

  const handleEditDialogClosed = async () => {
    setWallEditDialogOpen(false);
  };

  const handleFolderDialogClosed = async () => {
    setFolderEditDialogOpen(false);
  };

  const [newAnchorEl, setNewAnchorEl] = React.useState<null | HTMLElement>(
    null
  );
  const newOpen = Boolean(newAnchorEl);
  const handleNewClicked = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setNewAnchorEl(event.currentTarget);
    },
    []
  );
  const handleNewClose = () => {
    setNewAnchorEl(null);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        itemToDelete={
          selectedWallId
            ? `Wall ${selectedWall?.name}`
            : `Folder ${selectedFolder?.name}`
        }
        onSubmit={handleDeleteClicked}
        onClose={() => setDeleteDialogOpen(false)}
      />
      <WallEditDialog
        open={wallEditDialogOpen}
        handleClose={handleEditDialogClosed}
        selectedWallId={selectedWallId || -1}
      />
      <FolderEditDialog
        open={folderEditDialogOpen}
        handleClose={handleFolderDialogClosed}
        selectedFolder={selectedFolder}
      />
      <Grid container spacing={2}>
        <Grid container item xs={12} spacing={1}>
          <Grid item>
            <GreenButton name='New' onClick={handleNewClicked}></GreenButton>
            <Menu
              id='new-menu'
              anchorEl={newAnchorEl}
              open={newOpen}
              onClose={handleNewClose}
              MenuListProps={{
                'aria-labelledby': 'basic-button',
              }}
            >
              <MenuItem
                onClick={() => {
                  handleCreateWallClicked();
                  handleNewClose();
                }}
              >
                Wall
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleCreateFolderClicked();
                  handleNewClose();
                }}
              >
                Folder
              </MenuItem>
            </Menu>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='primary'
              onClick={handleEditClicked}
              disabled={
                !!(
                  (!selectedWallId && !selectedFolder) ||
                  (selectedWallId && selectedFolder)
                )
              }
            >
              Edit
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant='contained'
              color='error'
              onClick={handleDeleteDialogOpen}
              disabled={
                !!(
                  (!selectedWallId && !selectedFolder) ||
                  (selectedWallId && selectedFolder)
                )
              }
            >
              Delete
            </Button>
          </Grid>
          <Grid item>
            <ExportWallDialog />
          </Grid>
          <Grid item>
            <ImportWallDialog />
          </Grid>
        </Grid>
        <Grid item container lg={6} xs={8}>
          <Grid item component={Paper} xs>
            <List sx={{ overflow: 'auto', maxHeight: '78vh' }}>
              {folders?.map((folder) => (
                <React.Fragment key={folder.id + 'fragment'}>
                  <FolderListItem
                    folder={folder}
                    selected={folder.id === selectedFolder?.id}
                    handleFolderClicked={handleFolderClicked}
                    foldersOpenStatus={foldersOpenStatus}
                  />
                  <Collapse
                    in={foldersOpenStatus[folder.id]}
                    timeout='auto'
                    unmountOnExit
                  >
                    {folder.walls.map((wall) => (
                      <WallListItem
                        wall={wall}
                        selected={selectedWallId === wall.id}
                        handleWallClicked={handleWallClicked}
                        key={wall.id + 'wall'}
                        folder={folder}
                      />
                    ))}
                  </Collapse>
                </React.Fragment>
              ))}
              <WallsWithoutFoldersContainer
                handleWallClicked={handleWallClicked}
                selectedWallId={selectedWallId}
              />
            </List>
          </Grid>
        </Grid>
      </Grid>
    </DndProvider>
  );
};

export default ManageWallsPage;
